// @ts-strict-ignore
import { Vector3 } from 'three';
import { Dictionary } from 'typescript-collections';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { CompositeArticleConfigurationVO, CompositeConfigurationByBaseArticleVO, CompositeConfigurationByGivingAreasVO } from '@article/model/vo/composite-article-configuration.vo';
import { ProjectVO } from '@project/model/vo/project.vo';
import { PropertyVO } from '../components/article/model/vo/property.vo';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { CloneUtils } from '@shared/util/clone-utils';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { CompositeConnectionUtils } from '@project/components/article/util/composite-connection-utils';
import { DefinedMetaElementPropertyUtils } from '@article/util/defined-meta-element-property-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ProjectModel } from '@project/model/project.model';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { PropertyCreator } from '@article/util/property-creator';
import { ArticleManager } from '@article/manager/article.manager';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { RepresentationDataVO } from '@shared/vo/representation.vo';
import { BuildingBlockSaveDTO } from '../model/vo/building-block.vo';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { Injectable } from '@angular/core';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { UserSettings } from '@user/model/user-settings';
import { SelectionModelService } from '../components/article/service/selection-model.service';
import { RepresentationService } from './representation.service';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { ProjectDataService } from './project-data.service';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { lastValueFrom } from 'rxjs';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

@Injectable({ providedIn: 'root' })
export class SaveCompositeArticleService {
  private readonly EXCLUDED_PROPERTY_NAMES: string[] = [ArticleConnectionPropertyDef.PINS_COUNT, ArticleConnectionPropertyDef.TOTAL_DISTANCE];

  constructor(
    private projectModel: ProjectModel,
    private representationService: RepresentationService,
    private selectionModelService: SelectionModelService,
    private alertHelper: AlertHelper,
    private articleManager: ArticleManager,
    private projectDataService: ProjectDataService,
    private propertyCreator: PropertyCreator,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private containerService: ContainerService,
    private articleContainerUtils: ArticleContainerUtils,
    private userSettings: UserSettings,
    private articleUsageService: ArticleUsageService,
    private connectionInfoService: ConnectionInfoService
  ) {}

  public clearCompositeConnectionCorridor(project: ProjectVO): void {
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(project);
    for (const articleUsage of articleUsages) {
      if (!ArticleUsageUtils.isCompositePartArticleUsage(articleUsage)) {
        continue;
      }
      DefinedMetaElementPropertyUtils.removePropertiesByName(
        articleUsage.definedMetaElementInfoProperties,
        DefinedMetaElementInfoPropertyDef.CONNECTION_CORRIDOR
      );
    }
  }

  public async saveCompositeArticle(compositeArticleConfiguration: CompositeArticleConfigurationVO): Promise<ProjectVO> {
    this.clearCompositePropertiesFromProject();

    if (compositeArticleConfiguration.type == CompositeConfigurationByBaseArticleVO.CLASS_NAME) {
      return await this.saveBySingleBaseArticle(compositeArticleConfiguration as CompositeConfigurationByBaseArticleVO);
    } else if (compositeArticleConfiguration.type == CompositeConfigurationByGivingAreasVO.CLASS_NAME) {
      return await this.saveByMultipleBaseArticles(compositeArticleConfiguration as CompositeConfigurationByGivingAreasVO);
    }
  }

  private async saveBySingleBaseArticle(compositeArticleConfiguration: CompositeConfigurationByBaseArticleVO): Promise<ProjectVO> {
    const usageIds: string[] = this.findUsageIdsByArticleIdAndVersion(
      compositeArticleConfiguration.baseArticleId,
      compositeArticleConfiguration.baseArticleVersion
    );
    let baseUsageId: string = null;
    const configuredBaseUsageId: string = compositeArticleConfiguration.baseArticleUsageId;
    if (usageIds.length > 0) {
      baseUsageId = configuredBaseUsageId && usageIds.indexOf(configuredBaseUsageId) >= 0 ? configuredBaseUsageId : usageIds[0];
    }
    const baseArticleContainer: ArticleContainer = !baseUsageId ? null : this.containerService.findArticleContainerInGlobal(baseUsageId);
    if (baseArticleContainer) {
      const compositeArticleParts: ArticleContainer[] = this.articleContainerUtils.getConnectedArticlesRecursively(baseArticleContainer);
      compositeArticleParts.push(baseArticleContainer);
      this.markExludedArticlesInProject(ArticleContainerUtils.extractArticleGUIDs(compositeArticleParts));
      return await this.saveCompositeArticleCommand(this.projectModel.currentProject, compositeArticleConfiguration);
    } else {
      throw new Error("Base article container wasn't found!");
    }
  }

  private async saveByMultipleBaseArticles(compositeArticleConfiguration: CompositeConfigurationByGivingAreasVO): Promise<ProjectVO> {
    const compositeArticleParts: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    const baseContainers: ArticleContainer[] = ArticleContainerUtils.getBaseArticleContainers(compositeArticleParts);
    // refresh meta elements to restore all connections
    this.refreshContainers(baseContainers, false);
    try {
      let givingConnections: Connection[] = ConnectionInfoUtils.getGivingConnections(baseContainers);
      if (await this.validateSelectedContainers(givingConnections)) {
        givingConnections = this.sortGivingConnections(givingConnections);

        const connectionsInSets: Dictionary<string, Connection[]> = this.getConnectionsInSets(givingConnections);
        const compositeGivingAreas: MetaElementVO[] = this.createCompositeGivingConnectionAreas(connectionsInSets, givingConnections);
        this.markExludedArticlesInProject(ArticleContainerUtils.extractArticleGUIDs(compositeArticleParts));

        compositeArticleConfiguration.givingAreas = compositeGivingAreas;
        return await this.saveCompositeArticleCommand(this.projectModel.currentProject, compositeArticleConfiguration);
      } else {
        throw new Error("Selected articles are not valid!");
      }
    } finally {
      // refresh meta elements to remove all unused connections
      this.refreshContainers(baseContainers);
    }
  }

  public async saveAsBuildingBlock(project: ProjectVO): Promise<ProjectVO> {
    this.clearBuildingBlockPartPropertyFromProject();
    // get articles, that are connected to rail from selected articles
    const articlesConnectedToRail: ArticleContainer[] = this.getSelectedArticlesConnectedToRail();

    try {
      const givingConnections: Connection[] = ConnectionInfoUtils.getGivingConnections(articlesConnectedToRail);
      if (await this.validateSelectedContainers(givingConnections, true)) {
        const insertArticleHelper: InsertArticleHelper = this.insertArticleHelperFactory.createInstanceByConnectedArticleContainer(
          articlesConnectedToRail[0]
        );
        this.markIncludedArticlesToBuildingBlock(ArticleContainerUtils.extractArticleGUIDs(articlesConnectedToRail), false);
        this.setBuildingBlockStartPositionAndProjectTotalLengthProperties(project, insertArticleHelper, articlesConnectedToRail);
        return await this.saveBuildingBlockCommand(project);
      } else {
        throw new Error("Selected articles are not valid!");
      }
    } finally {
      // refresh meta elements to remove all unused connections
      this.refreshContainers(articlesConnectedToRail);
    }
  }

  private getSelectedArticlesConnectedToRail(): ArticleContainer[] {
    const compositeArticleParts: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    const baseContainers: ArticleContainer[] = ArticleContainerUtils.getBaseArticleContainers(compositeArticleParts);
    this.refreshContainers(baseContainers, false);

    const articlesconnectedToRail: ArticleContainer[] = [];
    baseContainers.forEach((baseContainer: ArticleContainer) => {
      const articleUsage: ArticleUsageVO = baseContainer.articleUsage;
      const backwardConnectedArticleUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsage);
      const backwardConnectedArticleUsages: ArticleUsageVO[] = this.projectModel.getArticleUsages(backwardConnectedArticleUsageIds);
      if (
        ArrayUtils.isNotEmpty(backwardConnectedArticleUsages) &&
        backwardConnectedArticleUsages.some((articleUsage) => this.articleUsageService.isMountingRailArticleUsage(articleUsage))
      ) {
        articlesconnectedToRail.push(baseContainer);
      }
    });
    return articlesconnectedToRail;
  }

  private setBuildingBlockStartPositionAndProjectTotalLengthProperties(
    project: ProjectVO,
    insertArticleHelper: InsertArticleHelper,
    articleContainers: ArticleContainer[]
  ): void {
    const startPoint: Vector3 = insertArticleHelper.startPoint.clone();
    let endPoint: Vector3 = startPoint.clone();
    let lastProcessedArticleLine: AdvancedLine = null;
    let offsetOnLine: Vector3;

    for (const articleContainer of articleContainers) {
      const articleLine: AdvancedLine = insertArticleHelper.getArticleLine(articleContainer).clone();

      if (!offsetOnLine) {
        offsetOnLine = articleLine.start.sub(endPoint);
      }
      endPoint = articleLine.end.sub(offsetOnLine);
      if (!lastProcessedArticleLine) {
        const articlePoistion: Vector3 = new Vector3().copy(articleContainer.position);
        articlePoistion.sub(offsetOnLine);
        const articleOffset: Vector3 = articlePoistion.sub(startPoint);
        project.infoProperties.setFloat("buildingBlockStartPosition", articleOffset.x);
        lastProcessedArticleLine = articleLine;
      }
    }
    const totalArticlesLength: number = startPoint.distanceTo(endPoint);
    project.infoProperties.setFloat("buildingBlockStartPosition", totalArticlesLength);
  }

  private async saveBuildingBlockCommand(project: ProjectVO): Promise<ProjectVO> {
    const buildingBlockHead = await lastValueFrom(this.projectDataService.getBuildingBlockHeadByName(project.name));
    const buildingBlockData: BuildingBlockSaveDTO = new BuildingBlockSaveDTO(-1, project.name, -1, project);

    if (buildingBlockHead) {
      buildingBlockData.name = buildingBlockHead.name;
      buildingBlockData.projectId = project.id ? project.id : -1;
      buildingBlockData.id = buildingBlockHead.id;
    }

    try {
      const buildingBlockSaveResponse = await lastValueFrom(this.projectDataService.saveBuildingBlock(buildingBlockData));
      this.buildingBlockSavedSuccess(buildingBlockSaveResponse.savedProject);
      return buildingBlockSaveResponse.savedProject;
    } catch (error) {
      this.buildingBlockSavedFailure(error, project.name);
      throw error;
    }
  }

  private async saveCompositeArticleCommand(project: ProjectVO, compositeConfiguration: CompositeArticleConfigurationVO): Promise<ProjectVO> {
    let representationData: RepresentationDataVO = null;
    if (project === this.projectModel.currentProject) {
      this.representationService.update();
      representationData = ProjectUtils.findFirstView3DRepresentationData(this.projectModel.currentProject);
      try {
        const projectVO = await lastValueFrom(this.projectDataService
          .saveCompositeArticle(compositeConfiguration, project, representationData));
        this.compositeArticleSavedSuccess(projectVO);
        return projectVO;
      } catch (error) {
        this.compositeArticleSavedFailure(error, project.name);
        throw error;
      }
    } else {
      throw new Error('Saving of a composite article related to another project different from currentProject is currently not supported!');
    }
  }

  private compositeArticleSavedSuccess(projectVO: ProjectVO): void {
    this.updateCurrentProject(projectVO);
    this.userSettings.saveLastSelectedProjectFromProjectVO(projectVO, true);
    this.projectModel.resetAdminProjectChanged();
  }

  private buildingBlockSavedSuccess(projectVO: ProjectVO): void {
    this.updateCurrentProject(projectVO);
  }

  private updateCurrentProject(project: ProjectVO): void {
    this.projectModel.currentProject.id = project.id;
    this.projectModel.currentProject.guid = project.guid;
    this.projectModel.currentProject.creationDate = project.creationDate;
    this.projectModel.currentProject.creationDateServerTimeStamp = project.creationDateServerTimeStamp;
    this.projectModel.currentProject.modificationDateServerTimeStamp = project.modificationDateServerTimeStamp;
    this.projectModel.currentProject.modificationDate = project.modificationDate;
    this.projectModel.currentProject.currentVersion = project.currentVersion;
    this.projectModel.currentProject.currentVersionDisplayString = project.currentVersionDisplayString;
    this.projectModel.currentProject.isLatestVersion = project.isLatestVersion;
    this.projectModel.currentProject.name = project.name;
  }

  private compositeArticleSavedFailure(e, projectName: string): void {
    this.alertHelper.showError(
      'save.title.error_saving_composite_article',
      'save.message.error_saving_composite_article',
      {translationParams: [projectName]}
    );
  }

  private buildingBlockSavedFailure(e, projectName: string): void {
    this.alertHelper.showError(
      'save.title.error_saving_building_block',
      'save.message.error_saving_building_block',
      {translationParams: [projectName]}
    );
  }

  private refreshContainers(containers: ArticleContainer[], deleteUnusedConnections: boolean = true): void {
    for (const container of containers) {
      container.refreshMetaElements(null, deleteUnusedConnections);
    }
  }

  private getConnectionsInSets(connections: Connection[]): Dictionary<string, Connection[]> {
    const result: Dictionary<string, Connection[]> = new Dictionary<string, Connection[]>();
    let setId: string;
    let connectionSetValues: Connection[];
    for (const connection of connections) {
      setId = MetaElementUtils.getConnectionSetId(connection.metaElementVO);
      connectionSetValues = result.getValue(setId);
      if (!connectionSetValues) {
        connectionSetValues = [connection];
      } else {
        connectionSetValues.push(connection);
      }
      result.setValue(setId, connectionSetValues);
    }
    return result;
  }

  private validateSelectedContainers(givingConnections: Connection[], isBuildingBlockSave: boolean = false): boolean {
    let isValid: boolean = true;
    if (ArrayUtils.isEmpty(givingConnections)) {
      isValid = false;
      const title: string = isBuildingBlockSave
        ? 'save.building_block.validation.title.articles_not_selected'
        : 'save.composite.validation.title.articles_not_selected';
      const message: string = isBuildingBlockSave
        ? 'save.building_block.validation.message.articles_not_selected'
        : 'save.composite.validation.message.articles_not_selected';
      this.alertHelper.showError(title, message);
    }
    return isValid;
  }

  private findUsageIdsByArticleIdAndVersion(articleId: number, articleVersion: number): string[] {
    const usageIds: string[] = [];
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(this.projectModel.currentProject);
    for (const articleUsage of articleUsages) {
      if (articleUsage.articleId === articleId && articleUsage.articleVersion === articleVersion) {
        usageIds.push(articleUsage.id);
      }
    }
    return usageIds;
  }

  private createCompositeGivingConnectionAreas(
    connectionsInSets: Dictionary<string, Connection[]>,
    sortedConnections: Connection[]
  ): MetaElementVO[] {
    const givingConnectionAreas: MetaElementVO[] = [];
    for (const connectionsInSet of connectionsInSets.values()) {
      let lastDistanceProperty: PropertyVO = null;
      for (let i = 0; i < connectionsInSet.length; i++) {
        const givingConnection: Connection = connectionsInSet[i];
        const connectionFeatureProperties: PropertyVO[] = this.connectionInfoService.findFeaturePropertiesByConnection(givingConnection);
        // exclude totalDistance and pinsCount properties, they are not kept here on composite article
        PropertyUtils.removePropertiesByNames(connectionFeatureProperties, this.EXCLUDED_PROPERTY_NAMES);
        // if (connectionFeatureProperties.length == 0) continue;
        if (i < connectionsInSet.length - 1) {
          // calculate distance only for connections that have further neighbor connection
          const nextGivingConnection: Connection = connectionsInSet[i + 1];
          const distance: number = MathUtils.calculateMiddlePointByRectangle(givingConnection.globalRectangle).distanceTo(
            MathUtils.calculateMiddlePointByRectangle(nextGivingConnection.globalRectangle)
          );
          const deltaDistance: number = this.getMaxDeltaDistance(connectionsInSet.slice(i === 0 ? i : i - 1, i + 2));
          let distanceProperty: PropertyVO;
          if (deltaDistance > 0) {
            distanceProperty = this.propertyCreator.createDistancePropertyWithRangeValue(distance, distance + deltaDistance);
          } else {
            distanceProperty = PropertyUtils.createDistanceProperty(distance);
          }
          lastDistanceProperty = distanceProperty;
          connectionFeatureProperties.push(distanceProperty);
        } else {
          // process last connection without neighbor
          if (lastDistanceProperty) {
            connectionFeatureProperties.push(lastDistanceProperty);
          }
        }

        const compositeGivingArea: MetaElementVO = this.cloneGivingArea(
          givingConnection,
          connectionFeatureProperties,
          sortedConnections.indexOf(givingConnection)
        );
        this.markCompositeGivingConnectionInUsage(givingConnection, compositeGivingArea.name);
        givingConnectionAreas.push(compositeGivingArea);
      }
    }

    return givingConnectionAreas;
  }

  private getMaxDeltaDistance(connections: Connection[]): number {
    let maxDistance: number = 0;
    for (const connection of connections) {
      const deltaDistance: number = ConnectionInfoUtils.getDeltaDistance(connection.metaElementVO);
      if (NumberUtils.isNumber(deltaDistance) && maxDistance < deltaDistance) {
        maxDistance = deltaDistance;
      }
    }
    return maxDistance;
  }

  private cloneGivingArea(givingConnection: Connection, newConnectionFeatures: PropertyVO[], connectionIndex: number): ConnectionAreaVO {
    const givingArea: ConnectionAreaVO = CloneUtils.cloneMetaElementVO(givingConnection.metaElementVO) as ConnectionAreaVO;
    // change connection name to make it unique inside composite article
    const connectionName: string = CompositeConnectionUtils.constructCompositeGivingConnectionName(givingArea.name, connectionIndex);
    givingArea.name = connectionName;
    givingArea.corridor = connectionIndex;
    givingArea.connectionFeatures = newConnectionFeatures;
    return givingArea;
  }

  private sortGivingConnections(givingConnections: Connection[]): Connection[] {
    const cubeIdToMiddlePoint: Dictionary<string, Vector3> = this.buildMiddleConnectionPointLookups(givingConnections);
    if (this.isEqualXPosition(givingConnections, cubeIdToMiddlePoint)) {
      return this.sortConnectionsByZAxis(givingConnections, cubeIdToMiddlePoint);
    } else {
      return this.sortConnectionsByXAxis(givingConnections, cubeIdToMiddlePoint);
    }
  }

  private sortConnectionsByXAxis(connections: Connection[], cubeIdToMiddlePoint: Dictionary<string, Vector3>): Connection[] {
    return this.sortConnectionsByAxis(connections, cubeIdToMiddlePoint, 'x');
  }

  private sortConnectionsByZAxis(connections: Connection[], cubeIdToMiddlePoint: Dictionary<string, Vector3>): Connection[] {
    return this.sortConnectionsByAxis(connections, cubeIdToMiddlePoint, 'z');
  }

  private sortConnectionsByAxis(connections: Connection[], cubeIdToMiddlePoint: Dictionary<string, Vector3>, axis: string): Connection[] {
    connections.sort(this.connectionsSortFunction(axis, cubeIdToMiddlePoint));
    return connections;
  }

  private connectionsSortFunction(sortAxis: string, connectionIdToMiddlePoint: Dictionary<string, Vector3>) {
    return (a: Connection, b: Connection): number => {
      const aMiddlePoint: Vector3 = connectionIdToMiddlePoint.getValue(a.uid);
      const bMiddlePoint: Vector3 = connectionIdToMiddlePoint.getValue(b.uid);
      switch (sortAxis) {
        case 'x':
          // compare bMiddlePoint with aMiddlePoint by x axis because in three.js is right-handed coordinate system
          return MathUtils.compare(bMiddlePoint.x, aMiddlePoint.x);
        case 'y':
          return MathUtils.compare(aMiddlePoint.y, bMiddlePoint.y);
        case 'z':
          return MathUtils.compare(aMiddlePoint.z, bMiddlePoint.z);
      }
    };
  }

  private isEqualXPosition(connections: Connection[], cubeIdToMiddlePoint: Dictionary<string, Vector3>): boolean {
    if (ArrayUtils.isEmpty(connections)) {
      return false;
    }
    const targetCube: Connection = connections[0];
    const targetMiddlePoint: Vector3 = cubeIdToMiddlePoint.getValue(targetCube.uid);
    for (let i = 1; i < connections.length; i++) {
      const connection: Connection = connections[i];
      const middlePoint: Vector3 = cubeIdToMiddlePoint.getValue(connection.uid);
      if (!MathUtils.equalsNumber(targetMiddlePoint.x, middlePoint.x)) {
        return false;
      }
    }
    return true;
  }

  private buildMiddleConnectionPointLookups(connections: Connection[]): Dictionary<string, Vector3> {
    const cubeIdToMiddlePoint: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    for (const connection of connections) {
      cubeIdToMiddlePoint.setValue(connection.uid, MathUtils.calculateMiddlePointByRectangle(connection.globalRectangle));
    }
    return cubeIdToMiddlePoint;
  }

  private markIncludedArticlesToBuildingBlock(usageIds: string[], isAccessory: boolean): void {
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(this.projectModel.currentProject);
    for (const articleUsage of articleUsages) {
      const articleVO: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
      if (usageIds.indexOf(articleUsage.id) !== -1 && (ArticleUtils.isArticleCanBeSavedAsBuildingBlock(articleVO) || isAccessory)) {
        ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(articleUsage, ArticleUsageInfoPropertyDef.PART_OF_BUILDING_BLOCK, true);
        if (!isAccessory) {
          ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(articleUsage, ArticleUsageInfoPropertyDef.PART_OF_BUILDING_BLOCK_ON_RAIL, true);
        }
        // recursive adding accessories to selected articles
        const articleUsageIds: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
        if (articleUsageIds.length > 0) {
          this.markIncludedArticlesToBuildingBlock(articleUsageIds, true);
        }
      }
    }
  }

  private markCompositeGivingConnectionInUsage(givingConnection: Connection, compositeGivingConnectionName: string): void {
    const articleUsage: ArticleUsageVO = givingConnection.articleContainer.articleUsage;
    if (ArrayUtils.isEmpty(articleUsage.infoProperties)) {
      articleUsage.infoProperties = [];
    }
    PropertyUtils.setStringPropertyValue(
      articleUsage.infoProperties,
      ArticleUsageInfoPropertyDef.COMPOSITE_GIVING_CONNECTION_NAME,
      compositeGivingConnectionName,
      false
    );
  }

  private markExludedArticlesInProject(compositeUsageIds: string[]): void {
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(this.projectModel.currentProject);
    for (const articleUsage of articleUsages) {
      if (compositeUsageIds.indexOf(articleUsage.id) === -1) {
        ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_PART_EXCLUDED, true);
      }
    }
  }

  public clearCompositePropertiesFromProject(): void {
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(this.projectModel.currentProject);
    this.clearArticleUsageInfoProperties(articleUsages, [
      ArticleUsageInfoPropertyDef.COMPOSITE_GIVING_CONNECTION_NAME,
      ArticleUsageInfoPropertyDef.COMPOSITE_PART_EXCLUDED,
    ]);
    this.clearArticleUsagePlausiProperties(articleUsages, [ArticleUsagePlausiPropertyDef.CUSTOM_APPLIED_ROTATION_ANGLE]);
  }

  public clearBuildingBlockPartPropertyFromProject(): void {
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(this.projectModel.currentProject);
    this.clearArticleUsageInfoProperties(articleUsages, [
      ArticleUsageInfoPropertyDef.PART_OF_BUILDING_BLOCK,
      ArticleUsageInfoPropertyDef.PART_OF_BUILDING_BLOCK_ON_RAIL,
    ]);
  }

  private clearArticleUsageInfoProperties(articleUsages: ArticleUsageVO[], propertyNames: string[]): void {
    for (const usage of articleUsages) {
      PropertyUtils.removePropertiesByNames(usage.infoProperties, propertyNames);
    }
  }

  private clearArticleUsagePlausiProperties(articleUsages: ArticleUsageVO[], propertyNames: string[]): void {
    for (const usage of articleUsages) {
      PropertyUtils.removePropertiesByNames(usage.plausiProperties, propertyNames);
    }
  }

  public updateCompositeConnectionCorridor(project: ProjectVO): void {
    const processedCompositeUsageIds: string[] = [];
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(project);
    for (const articleUsage of articleUsages) {
      const compositeUsageId: string = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(articleUsage);
      if (compositeUsageId == null || processedCompositeUsageIds.indexOf(compositeUsageId) >= 0) {
        continue;
      }
      processedCompositeUsageIds.push(compositeUsageId);
      const compositeArticle: ArticleVO = this.compositeArticleInfoUtils.getCompositeArticleFromUsageByArticleNo(articleUsage);
      if (!compositeArticle || ArticleUtils.getConnectionElements(compositeArticle, ConnectionAreaVO.SIDE_GIVING).length < 2) {
        continue;
      }
      const compositePartContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
      const partContainers: ArticleContainer[] = this.containerService.getCompositePartArticles(compositePartContainer);
      const baseContainers: ArticleContainer[] = ArticleContainerUtils.getBaseArticleContainers(partContainers);
      if (!this.isGivingConnectionNamePropertyExists(baseContainers)) {
        this.applyCompositeConnectionCorridorProperty(baseContainers);
      }
    }
  }

  private applyCompositeConnectionCorridorProperty(baseCompositeParts: ArticleContainer[]): void {
    this.refreshContainers(baseCompositeParts, false);
    let givingConnections: Connection[] = ConnectionInfoUtils.getGivingConnections(baseCompositeParts);
    givingConnections = this.sortGivingConnections(givingConnections);
    if (this.isCompositeArticleRotated(baseCompositeParts)) {
      givingConnections.reverse();
    }
    this.setCorridorPropertyBySortedConnections(givingConnections);
    this.refreshContainers(baseCompositeParts);
  }

  private setCorridorPropertyBySortedConnections(sortedConnections: Connection[]): void {
    for (let i = 0; i < sortedConnections.length; i++) {
      const connection: Connection = sortedConnections[i];
      const articleContainer: ArticleContainer = connection.articleContainer;
      ArticleUsageUtils.setDefinedMetaElementInfoPropertyIntValue(
        articleContainer.articleUsage,
        connection.connectionName,
        DefinedMetaElementInfoPropertyDef.CONNECTION_CORRIDOR,
        i
      );
    }
  }

  private isCompositeArticleRotated(baseCompositeParts: ArticleContainer[]): boolean {
    if (ArrayUtils.isEmpty(baseCompositeParts)) {
      return false;
    }
    const partContainer: ArticleContainer = baseCompositeParts[0];
    const parentConnectionIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(partContainer.articleUsage);
    if (ArrayUtils.isEmpty(parentConnectionIds)) {
      return false;
    }
    const parentConnectionIdentity: MetaElementUsageIdentityVO = parentConnectionIds[0];
    const parentConnection: Connection = this.connectionInfoService.findConnectionByIdentity(parentConnectionIdentity);
    if (!parentConnection) {
      return false;
    }
    return this.isConnectionRectangleRotated(parentConnection.globalRectangle);
  }

  private isConnectionRectangleRotated(connectionRectangle: Rectangle3D): boolean {
    const firstPoint: Vector3 = connectionRectangle.point1;
    const middlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(connectionRectangle);
    return MathUtils.compare(firstPoint.x, middlePoint.x) === 1 ? true : false;
  }

  private isGivingConnectionNamePropertyExists(articleContainers: ArticleContainer[]): boolean {
    for (const container of articleContainers) {
      const compositeConnectionNames: string[] = CompositeConnectionUtils.getCompositeGivingConnectionNames(container.articleUsage);
      if (compositeConnectionNames.length > 0) {
        return true;
      }
    }
    return false;
  }
}
